import React, { useState, useMemo, useCallback, useRef } from "react";
import Icon1 from "@assets/icons/Icon";
import { useDataStore } from "@store/user_org";
import { useDispoAtelierStore, ZONE_COLORS } from "@store/dispoAtelierStore";
import { getCachedWorkers } from "@store/atelierStore";
import type { AvailabilityBlock, ZoneType } from "@store/disponibiliteStore";
import { getMonday, getWeekDates } from "@store/disponibiliteStore";
import { snapToGrid } from "@utils/timeGrid";
import { SNAP_HOUR } from "../../atelier/gridTypes";
import { BlockModal } from "./BlockModal";
import { computeOverlapLayout } from "./overlapLayout";
import { useFullClosures, isDateClosed } from "@utils/closedDates";
import { blockSegmentOnDate, type DaySegment } from "@utils/blockSegments";
import { ClosedDayOverlay } from "@components/ui/ClosedDayOverlay";

const HOUR_BLOCK_HEIGHT = 80;

const DAY_NAMES_SHORT = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function getBlockColors(block: AvailabilityBlock) {
  const zone = block.zoneType || "mecanique";
  return ZONE_COLORS[zone] || ZONE_COLORS.mecanique;
}

function formatHour(h: number): string {
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${hours}h${mins > 0 ? String(mins).padStart(2, "0") : ""}`;
}

function formatDayHeader(dateStr: string, index: number): string {
  const d = new Date(dateStr + "T00:00:00");
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${DAY_NAMES_SHORT[index]} ${day}/${month}`;
}

interface BlockDragState {
  blockId: string;
  mode: "move" | "resize";
  startY: number;
  origStartHour: number;
  origDuration: number;
  dayIndex: number;
  origDayIndex: number;
  blockRecurring: boolean;
  blockDayOfWeek?: number;
  blockDate: string;
}

interface CreateDragState {
  dayIndex: number;
  startQuarter: number;
  currentQuarter: number;
}

interface DragPreview {
  blockId?: string;
  dayIndex: number;
  startHour: number;
  durationHours: number;
}

export function WeekTimeGrid({
  onEditBlock,
}: {
  onEditBlock: (block: AvailabilityBlock) => void;
}) {
  const orgData = useDataStore((s) => s.orgData);
  // Anchor the visual grid on whole hours: a fractional opening hour (e.g. 7h45 =
  // 7.75) must not shift the hour labels/lines to ".75" positions. Blocks are then
  // positioned relative to this whole-hour origin so they line up with the labels.
  const HOURS_START = Math.floor(orgData?.config?.booking_opening_hour ?? 8);
  const HOURS_END = Math.ceil(Math.max((orgData?.config?.booking_closing_hour ?? 18) + 2, 20));
  const QUARTER_HEIGHT = HOUR_BLOCK_HEIGHT / 4; // visual 15-min grid lines
  // Snap step matches the planning atelier granularity.
  const SNAP_STEP = SNAP_HOUR;
  const STEP_HEIGHT = HOUR_BLOCK_HEIGHT * SNAP_STEP;

  const selectedDate = useDispoAtelierStore((s) => s.selectedDate);
  const selectedWorkerIds = useDispoAtelierStore((s) => s.selectedWorkerIds);
  const blocks = useDispoAtelierStore((s) => s.blocks);
  const closedDates = useFullClosures();
  const searchQuery = useDispoAtelierStore((s) => s.searchQuery);
  const activeZoneFilters = useDispoAtelierStore((s) => s.activeZoneFilters);
  const updateBlock = useDispoAtelierStore((s) => s.updateBlock);

  const [createModal, setCreateModal] = useState<{
    date: string;
    startHour: number;
    duration: number;
  } | null>(null);

  const blockDragRef = useRef<BlockDragState | null>(null);
  const createDragRef = useRef<CreateDragState | null>(null);
  const didDrag = useRef(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);

  const monday = useMemo(() => getMonday(selectedDate), [selectedDate]);
  const weekDates = useMemo(() => getWeekDates(monday), [monday]);

  const hours = useMemo(() => {
    const arr: number[] = [];
    for (let h = HOURS_START; h < HOURS_END; h++) arr.push(h);
    return arr;
  }, []);

  const totalGridHeight = (HOURS_END - HOURS_START) * HOUR_BLOCK_HEIGHT;

  // Per weekday (Mon–Fri), the blocks that have a segment on that day (multi-day
  // and all-day spans appear on every covered day).
  const itemsByDay = useMemo(() => {
    const map: Record<number, { block: AvailabilityBlock; seg: DaySegment }[]> = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    for (const b of blocks) {
      if (!selectedWorkerIds.includes(b.workerId)) continue;
      if (activeZoneFilters.length > 0 && (!b.zoneType || !activeZoneFilters.includes(b.zoneType))) continue;
      if (searchQuery && !b.label.toLowerCase().includes(searchQuery.toLowerCase())) continue;
      for (let i = 0; i < 5; i++) {
        const seg = blockSegmentOnDate(b, weekDates[i], HOURS_START, HOURS_END);
        if (seg) map[i].push({ block: b, seg });
      }
    }
    return map;
  }, [blocks, selectedWorkerIds, weekDates, searchQuery, activeZoneFilters, HOURS_START, HOURS_END]);

  const layoutByDay = useMemo(() => {
    const result: Record<number, Map<string, { colIndex: number; colCount: number }>> = {};
    for (let i = 0; i < 7; i++) {
      result[i] = computeOverlapLayout(
        (itemsByDay[i] || []).map(({ block, seg }) => ({
          ...block,
          startHour: seg.startHour,
          durationHours: seg.endHour - seg.startHour,
        })),
      );
    }
    return result;
  }, [itemsByDay]);

  // Congé intervals per day & worker — used to flag overlapping work zones as
  // neutralised (faded + hatched in the congé colour).
  const congeByDay = useMemo(() => {
    const res: Record<number, Record<string, [number, number][]>> = {};
    for (let i = 0; i < 7; i++) {
      const m: Record<string, [number, number][]> = {};
      for (const { block, seg } of itemsByDay[i] || []) {
        if (block.zoneType === "conge") {
          if (!m[block.workerId]) m[block.workerId] = [];
          m[block.workerId].push([seg.startHour, seg.endHour]);
        }
      }
      res[i] = m;
    }
    return res;
  }, [itemsByDay]);

  const multipleWorkersSelected = selectedWorkerIds.length > 1;

  const getDayIndexFromX = useCallback((clientX: number): number => {
    const grid = gridRef.current;
    if (!grid) return 0;
    const rect = grid.getBoundingClientRect();
    const hourLabelWidth = 60;
    const colsWidth = rect.width - hourLabelWidth;
    const relX = clientX - rect.left - hourLabelWidth;
    const colWidth = colsWidth / 5;
    return Math.max(0, Math.min(4, Math.floor(relX / colWidth)));
  }, []);

  const handleBlockPointerDown = useCallback(
    (e: React.PointerEvent, block: AvailabilityBlock, dayIndex: number, mode: "move" | "resize") => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      didDrag.current = false;
      blockDragRef.current = {
        blockId: block.id,
        mode,
        startY: e.clientY,
        origStartHour: block.startHour,
        origDuration: block.durationHours,
        dayIndex,
        origDayIndex: dayIndex,
        blockRecurring: block.recurring,
        blockDayOfWeek: block.dayOfWeek,
        blockDate: block.date,
      };
      setDragPreview({
        blockId: block.id,
        dayIndex,
        startHour: block.startHour,
        durationHours: block.durationHours,
      });
    },
    [],
  );

  const handleBlockPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const bd = blockDragRef.current;
      if (!bd) return;
      const deltaY = e.clientY - bd.startY;
      if (Math.abs(deltaY) > 3) didDrag.current = true;
      const deltaSteps = Math.round(deltaY / STEP_HEIGHT);
      const deltaHours = deltaSteps * SNAP_STEP;

      if (bd.mode === "resize") {
        const newDur = snapToGrid(bd.origDuration + deltaHours, SNAP_STEP);
        const clamped = Math.max(SNAP_STEP, Math.min(newDur, HOURS_END - bd.origStartHour));
        setDragPreview({ blockId: bd.blockId, dayIndex: bd.dayIndex, startHour: bd.origStartHour, durationHours: clamped });
      } else {
        const newStart = snapToGrid(bd.origStartHour + deltaHours, SNAP_STEP);
        const clamped = Math.max(HOURS_START, Math.min(newStart, HOURS_END - bd.origDuration));
        const newDayIndex = getDayIndexFromX(e.clientX);
        if (newDayIndex !== bd.dayIndex) {
          didDrag.current = true;
          bd.dayIndex = newDayIndex;
        }
        setDragPreview({ blockId: bd.blockId, dayIndex: bd.dayIndex, startHour: clamped, durationHours: bd.origDuration });
      }
    },
    [getDayIndexFromX],
  );

  const handleBlockPointerUp = useCallback(() => {
    const bd = blockDragRef.current;
    if (!bd) return;
    if (dragPreview && didDrag.current) {
      const updates: Partial<Omit<AvailabilityBlock, "id">> = {
        startHour: dragPreview.startHour,
        durationHours: dragPreview.durationHours,
      };
      if (bd.dayIndex !== bd.origDayIndex) {
        const newDate = weekDates[bd.dayIndex];
        updates.date = newDate;
        if (bd.blockRecurring) updates.dayOfWeek = bd.dayIndex;
      }
      updateBlock(bd.blockId, updates);
    }
    blockDragRef.current = null;
    setDragPreview(null);
  }, [dragPreview, updateBlock, weekDates]);

  const handleCellPointerDown = useCallback(
    (e: React.PointerEvent, dayIndex: number) => {
      if (blockDragRef.current) return;
      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const relY = e.clientY - rect.top;
      const step = Math.floor(relY / STEP_HEIGHT);
      createDragRef.current = { dayIndex, startQuarter: step, currentQuarter: step };
      const startHour = HOURS_START + step * SNAP_STEP;
      setDragPreview({ dayIndex, startHour, durationHours: SNAP_STEP });
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [],
  );

  const handleCellPointerMove = useCallback(
    (e: React.PointerEvent, dayIndex: number) => {
      if (blockDragRef.current) { handleBlockPointerMove(e); return; }
      const cd = createDragRef.current;
      if (!cd || cd.dayIndex !== dayIndex) return;
      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const relY = Math.max(0, Math.min(e.clientY - rect.top, totalGridHeight));
      const step = Math.floor(relY / STEP_HEIGHT);
      cd.currentQuarter = step;
      const start = Math.min(cd.startQuarter, step);
      const end = Math.max(cd.startQuarter, step) + 1;
      setDragPreview({ dayIndex, startHour: HOURS_START + start * SNAP_STEP, durationHours: (end - start) * SNAP_STEP });
    },
    [totalGridHeight, handleBlockPointerMove],
  );

  const handleCellPointerUp = useCallback(
    (_e: React.PointerEvent, dayIndex: number) => {
      if (blockDragRef.current) { handleBlockPointerUp(); return; }
      const cd = createDragRef.current;
      if (!cd || cd.dayIndex !== dayIndex) return;
      const start = Math.min(cd.startQuarter, cd.currentQuarter);
      const end = Math.max(cd.startQuarter, cd.currentQuarter) + 1;
      const startHour = HOURS_START + start * SNAP_STEP;
      const duration = (end - start) * SNAP_STEP;
      if (duration >= SNAP_STEP && selectedWorkerIds.length > 0) {
        setCreateModal({ date: weekDates[dayIndex], startHour, duration });
      }
      createDragRef.current = null;
      setDragPreview(null);
    },
    [weekDates, handleBlockPointerUp, selectedWorkerIds],
  );

  return (
    <div className="flex flex-col flex-1" style={{ minWidth: 0 }} onPointerMove={handleBlockPointerMove} onPointerUp={handleBlockPointerUp}>
      {/* Day headers */}
      <div className="flex flex-row" style={{ paddingLeft: 60 }}>
        {weekDates.slice(0, 5).map((date, i) => (
          <div key={date} className="flex-1 text-center text-h4 font-medium py-2" style={{ fontFamily: "Poppins", color: "#737373", borderBottom: "1px solid #E5E5E5", borderRight: i < 4 ? "1px solid #E5E5E5" : undefined }}>
            {formatDayHeader(date, i)}
          </div>
        ))}
      </div>

      {/* Grid body */}
      <div ref={gridRef} className="flex flex-row flex-1 overflow-y-auto" style={{ position: "relative" }}>
        {/* Hour labels */}
        <div className="flex flex-col flex-shrink-0" style={{ width: 60 }}>
          {hours.map((h) => (
            <div key={h} className="flex flex-col" style={{ height: HOUR_BLOCK_HEIGHT }}>
              {[0, 15, 30, 45].map((m) => (
                <div key={m} className="flex flex-row items-start" style={{ height: QUARTER_HEIGHT }}>
                  {m === 0 ? (
                    <>
                      <span className="text-h5 font-medium pr-1 text-right" style={{ width: 30, fontFamily: "Poppins", color: "#A1A1A1", marginTop: -6 }}>{h}h</span>
                      <span className="text-h6 font-medium" style={{ fontFamily: "Poppins", color: "#D4D4D4", marginTop: -4 }}>00</span>
                    </>
                  ) : (
                    <>
                      <span style={{ width: 30 }} />
                      <span className="text-h6 font-medium" style={{ fontFamily: "Poppins", color: "#E5E5E5", marginTop: -4 }}>{m}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Day columns (Mon-Fri) */}
        {weekDates.slice(0, 5).map((date, dayIndex) => {
          const dayItems = itemsByDay[dayIndex] || [];
          const preview = dragPreview && dragPreview.dayIndex === dayIndex ? dragPreview : null;

          return (
            <div
              key={date}
              className="flex-1 relative"
              style={{ borderRight: dayIndex < 4 ? "1px solid #E5E5E5" : undefined, cursor: "crosshair" }}
              onPointerDown={(e) => handleCellPointerDown(e, dayIndex)}
              onPointerMove={(e) => handleCellPointerMove(e, dayIndex)}
              onPointerUp={(e) => handleCellPointerUp(e, dayIndex)}
            >
              {/* Grid lines */}
              {hours.map((h) => (
                <div key={h} style={{ height: HOUR_BLOCK_HEIGHT, borderBottom: "1px solid #E5E5E5" }}>
                  {[0, 1, 2, 3].map((q) => (
                    <div key={q} style={{ height: QUARTER_HEIGHT, borderBottom: q < 3 ? "1px solid #F5F5F5" : undefined }} />
                  ))}
                </div>
              ))}

              {/* Org closure day indicator */}
              {isDateClosed(date, closedDates) && <ClosedDayOverlay />}

              {/* Create-drag preview */}
              {preview && !preview.blockId && (
                <div className="absolute left-1 right-1 pointer-events-none" style={{ top: (preview.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT, height: preview.durationHours * HOUR_BLOCK_HEIGHT, background: "rgba(99, 102, 241, 0.15)", border: "2px dashed #6366F1", borderRadius: 6, zIndex: 30 }} />
              )}

              {/* Move-drag preview (ghost in destination column) */}
              {preview && preview.blockId && (() => {
                const srcBlock = blocks.find((b) => b.id === preview.blockId);
                if (!srcBlock) return null;
                const colors = getBlockColors(srcBlock);
                const topPx = (preview.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
                const heightPx = preview.durationHours * HOUR_BLOCK_HEIGHT;
                return (
                  <div className="absolute left-1 right-1 pointer-events-none flex flex-col overflow-hidden" style={{ top: topPx, height: Math.max(heightPx, QUARTER_HEIGHT), background: colors.bg, border: `1px solid ${colors.border}`, boxShadow: "0px 4px 12px rgba(0,0,0,0.2)", borderRadius: 6, padding: "4px 6px", zIndex: 35, opacity: 0.85 }}>
                    <span className="text-h5 font-medium" style={{ color: colors.text, lineHeight: "140%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{srcBlock.label}</span>
                    {heightPx >= 50 && <span className="text-h6" style={{ color: colors.text, opacity: 0.7 }}>{formatHour(preview.startHour)} – {formatHour(preview.startHour + preview.durationHours)}</span>}
                  </div>
                );
              })()}

              {/* Blocks */}
              {dayItems.map(({ block, seg }) => {
                const colors = getBlockColors(block);
                // A work zone overlapping a congé is neutralised → faded + hatched.
                const neutralized = block.zoneType !== "conge" &&
                  (congeByDay[dayIndex]?.[block.workerId] || []).some(([cs, ce]) => seg.startHour < ce && seg.endHour > cs);
                // Multi-day / all-day blocks render as a fixed segment (edit via modal).
                const multiSpan = !!block.allDay || !seg.isFirstDay || !seg.isLastDay || !!block.endDate;
                const isDragging = dragPreview?.blockId === block.id && !multiSpan;
                const isDraggedAway = isDragging && dragPreview!.dayIndex !== dayIndex;
                const displayStart = isDragging && !isDraggedAway ? dragPreview!.startHour : seg.startHour;
                const displayDur = isDragging && !isDraggedAway ? dragPreview!.durationHours : seg.endHour - seg.startHour;
                const topPx = (displayStart - HOURS_START) * HOUR_BLOCK_HEIGHT;
                const heightPx = displayDur * HOUR_BLOCK_HEIGHT;

                // Overlap layout positioning
                const layout = layoutByDay[dayIndex]?.get(block.id);
                const colIndex = layout?.colIndex ?? 0;
                const colCount = layout?.colCount ?? 1;
                const leftPct = (colIndex / colCount) * 100;
                const widthPct = 100 / colCount;

                const workerName = multipleWorkersSelected
                  ? getCachedWorkers().find((w) => w.id === block.workerId)?.name
                  : undefined;

                return (
                  <div
                    key={block.id}
                    className="absolute flex flex-col overflow-hidden"
                    style={{
                      top: topPx,
                      height: Math.max(heightPx, QUARTER_HEIGHT),
                      left: `calc(${leftPct}% + 1px)`,
                      width: `calc(${widthPct}% - 2px)`,
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      boxShadow: isDragging && !isDraggedAway ? "0px 4px 12px rgba(0,0,0,0.2)" : "0px 2px 4px rgba(0,0,0,0.08)",
                      borderRadius: 6,
                      padding: "4px 6px",
                      zIndex: isDragging ? 35 : 20,
                      userSelect: "none",
                      opacity: isDraggedAway ? 0.3 : isDragging ? 0.85 : neutralized ? 0.5 : 1,
                      transition: isDragging ? undefined : "opacity 0.15s",
                      cursor: multiSpan ? "pointer" : "grab",
                    }}
                    onPointerDown={(e) => {
                      // Always stop propagation so the column underneath doesn't also
                      // start a "create slot" drag (multi-day blocks skip the move drag).
                      e.stopPropagation();
                      if (!multiSpan) handleBlockPointerDown(e, block, dayIndex, "move");
                    }}
                    onClick={(e) => { e.stopPropagation(); if (multiSpan || !didDrag.current) onEditBlock(block); }}
                  >
                    {/* Neutralised by a congé — hatch in the congé colour */}
                    {neutralized && (
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          borderRadius: 6,
                          background: `repeating-linear-gradient(45deg, ${ZONE_COLORS.conge.text}33, ${ZONE_COLORS.conge.text}33 5px, transparent 5px, transparent 11px)`,
                        }}
                        title="Ignoré (congé)"
                      />
                    )}
                    {workerName && (
                      <span className="text-h6 font-semibold" style={{ color: colors.text, opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "130%" }}>
                        {workerName}
                      </span>
                    )}
                    <span className="text-h5 font-medium" style={{ color: colors.text, lineHeight: "140%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{block.label || ZONE_COLORS[block.zoneType || "mecanique"].label}</span>
                    {heightPx >= 50 && !block.allDay && <span className="text-h6" style={{ color: colors.text, opacity: 0.7 }}>{formatHour(displayStart)} – {formatHour(displayStart + displayDur)}</span>}
                    {heightPx >= 40 && (
                      <div className="absolute bottom-1 right-1 flex flex-row gap-1" style={{ zIndex: 25 }}>
                        {(block.recurrence === "annual" || block.recurring) && (
                          <div className="flex items-center justify-center rounded" style={{ width: 18, height: 18, background: "rgba(255,255,255,0.6)" }}>
                            <Icon1 name="restart" size={12} color={colors.text} />
                          </div>
                        )}
                      </div>
                    )}
                    {/* Resize handle (single-day blocks only) */}
                    {!multiSpan && (
                      <div
                        className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                        style={{ height: 8, cursor: "ns-resize" }}
                        onPointerDown={(e) => { e.stopPropagation(); handleBlockPointerDown(e, block, dayIndex, "resize"); }}
                      >
                        <div style={{ width: 20, height: 2, borderRadius: 1, background: colors.text, opacity: 0.4 }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {createModal && (
        <BlockModal
          mode="create"
          workerId={selectedWorkerIds[0]}
          selectedWorkerIds={selectedWorkerIds}
          initialDate={createModal.date}
          initialStartHour={createModal.startHour}
          initialDuration={createModal.duration}
          onClose={() => setCreateModal(null)}
        />
      )}
    </div>
  );
}
